import React, { useMemo, useState } from 'react'
import { Box, Card, CardContent, Collapse, Divider, Grid, Typography, Button, TextField, InputAdornment, Chip } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Clear'
import { useRecoilValue } from 'recoil'
import Layout from '../components/layout/Layout'
import { userInfoState } from '../recoil/atoms'
import { HELP_BY_ROLE, faqFor, ROLE_LABEL, TOUR_STEPS, activeRoleOf } from '../assets/helpContent'
import GuidedTour from '../components/help/GuidedTour'
import { toFarsiDigits } from '../assets/utils'

const useStyles = makeStyles(() => ({
  root: { padding: '6px 4px 40px' },
  hero: {
    background: 'linear-gradient(135deg, #1a237e 0%, #4b3fa7 100%)',
    color: '#fff',
    borderRadius: 14,
    padding: '20px 22px',
    marginBottom: 18,
  },
  searchWrap: { marginBottom: 16 },
  card: {
    borderRadius: 14,
    border: '1px solid #e3e6f2',
    background: '#fff',
    marginBottom: 14,
    overflow: 'hidden',
  },
  header: {
    background: '#f4f6fb',
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
  },
  section: { background: '#f9fafd', border: '1px solid #eef0f8', borderRadius: 10, padding: 12, marginTop: 8 },
  tip: {
    background: '#fff8e1', border: '1px dashed #f9a825', borderRadius: 10,
    padding: '8px 10px', marginTop: 8, fontSize: 12.5, color: '#7a5c00',
  },
  faq: { background: '#f9fafd', border: '1px solid #eef0f8', borderRadius: 10, padding: 12, marginTop: 8 },
  empty: { textAlign: 'center', padding: 26, color: '#8a92b2' },
}))

/** یکسان‌سازیِ متن برای جستجو: ی/ك عربی → فارسی، نیم‌فاصله → فاصله */
const norm = (s) => String(s || '')
  .replace(/[\u064A\u0649]/g, '\u06CC')
  .replace(/\u0643/g, '\u06A9')
  .replace(/[\u200c\u200f\u200e]/g, ' ')
  .replace(/[\u064B-\u0652]/g, '')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase()

/** همهٔ واژه‌های جستجو باید در متن باشند (AND) */
const matches = (haystack, tokens) => {
  if (!tokens.length) return true
  const h = norm(haystack)
  return tokens.every((t) => h.includes(t))
}

/** متنِ کاملِ یک صفحهٔ راهنما (برای جستجو) */
const haystackOf = (h) => {
  if (!h) return ''
  const parts = [h.title, h.intro]
  ;(h.sections || []).forEach((s) => {
    parts.push(s.title)
    ;(s.body || []).forEach((b) => parts.push(b))
    if (s.tip) parts.push(s.tip)
  })
  return parts.join(' ')
}

const HelpPage = () => {
  const classes = useStyles()
  const userInfo = useRecoilValue(userInfoState)
  const role = activeRoleOf(userInfo)
  const myHelp = HELP_BY_ROLE[role] || HELP_BY_ROLE.Contractor
  const myRoutes = Object.keys(myHelp)
  const myFaq = faqFor(role)
  const [openFaq, setOpenFaq] = useState(-1)
  const [tourOpen, setTourOpen] = useState(false)
  const [query, setQuery] = useState('')

  const tokens = useMemo(
    () => norm(query).split(' ').filter(Boolean),
    [query]
  )
  const searching = tokens.length > 0

  // صفحه‌های راهنما که با جستجو می‌خوانند
  const visibleRoutes = useMemo(
    () => myRoutes.filter((r) => {
      const h = myHelp[r]
      if (!h || !h.sections || !h.sections.length) return false
      if (!searching) return true
      return matches(haystackOf(h), tokens)
    }),
    [myRoutes, myHelp, tokens, searching]
  )

  // سؤالهای متداول که با جستجو می‌خوانند
  const visibleFaq = useMemo(
    () => myFaq.filter((f) => (searching ? matches(`${f.q} ${f.a}`, tokens) : true)),
    [myFaq, tokens, searching]
  )

  const startTour = () => {
    sessionStorage.removeItem('tour_done_' + role)
    sessionStorage.setItem('tour_active', '1')
    sessionStorage.setItem('tour_step', '0')
    window.location.href = '/'
  }

  return (
    <Layout>
      <Box className={classes.root}>
        <Box className={classes.hero}>
          <Typography style={{ fontWeight: 800, fontSize: 20 }}>
            📖 راهنمای کامل سامانه ارجان پیمان
          </Typography>
          <Typography style={{ fontSize: 13, marginTop: 6, opacity: 0.92 }}>
            نقش شما: {ROLE_LABEL[role] || role} — راهنمای بخش‌های مرتبط با نقش شما + سؤالهای متداول
          </Typography>
          <Box style={{ marginTop: 12 }}>
            <Button size="small" variant="contained"
              onClick={startTour}
              style={{ background: '#fff', color: '#1a237e', fontWeight: 700 }}>
              🎓 اجرای دوبارهٔ تور آموزشی
            </Button>
          </Box>
        </Box>

        {/* جستجو در راهنما */}
        <Box className={classes.searchWrap}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="🔍 جستجو در راهنما (مثلاً: صورت‌وضعیت، آپلود، پیام، اشتراک، مدیر سازمان ...)"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: '#8a92b2' }} />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <Button size="small" onClick={() => setQuery('')} style={{ minWidth: 0, padding: 2 }}>
                    <ClearIcon style={{ fontSize: 18, color: '#8a92b2' }} />
                  </Button>
                </InputAdornment>
              ) : null,
            }}
          />
          {searching && (
            <Box display="flex" alignItems="center" gap={8} style={{ marginTop: 8 }} flexWrap="wrap">
              <Chip size="small" label={`${toFarsiDigits(visibleRoutes.length)} صفحه و ${toFarsiDigits(visibleFaq.length)} سؤال`}
                style={{ background: '#e8eaf6', color: '#283593', fontWeight: 700 }} />
              <Button size="small" onClick={() => setQuery('')} style={{ color: '#5c6370' }}>
                پاک کردن جستجو
              </Button>
            </Box>
          )}
        </Box>

        {/* راهنمای صفحات مرتبط با نقش */}
        <Grid container spacing={2}>
          {visibleRoutes.map((route) => {
            const h = myHelp[route]
            return (
              <Grid item xs={12} md={6} key={route}>
                <Card className={classes.card} elevation={0}>
                  <Box style={{ padding: '12px 16px 4px' }}>
                    <Typography style={{ fontWeight: 800, fontSize: 15, color: '#1a237e' }}>
                      {h.icon} {h.title}
                    </Typography>
                    <Typography style={{ fontSize: 12.5, color: '#5c6370', marginTop: 4 }}>
                      {h.intro}
                    </Typography>
                  </Box>
                  <CardContent style={{ paddingTop: 8 }}>
                    {h.sections.map((s, i) => (
                      <Box key={i} className={classes.section}>
                        <Typography style={{ fontWeight: 800, fontSize: 13, color: '#1a237e', marginBottom: 6 }}>
                          {s.title}
                        </Typography>
                        {s.body.map((b, j) => (
                          <Typography key={j} style={{ fontSize: 12.5, color: '#43476a', lineHeight: 1.9 }}>
                            • {b}
                          </Typography>
                        ))}
                        {s.tip && <Box className={classes.tip}>💡 {s.tip}</Box>}
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            )
          })}
        </Grid>

        {searching && visibleRoutes.length === 0 && visibleFaq.length === 0 && (
          <Box className={classes.empty}>
            نتیجه‌ای برای «{query}» پیدا نشد. عبارتِ دیگری را امتحان کنید.
          </Box>
        )}

        {/* سؤالهای متداول — مخصوصِ نقشِ کاربر */}
        {visibleFaq.length > 0 && (
          <Card className={classes.card} elevation={0}>
            <Box style={{ padding: '12px 16px' }}>
              <Typography style={{ fontWeight: 800, fontSize: 15, color: '#1a237e' }}>
                ❓ سؤالهای متداول — {ROLE_LABEL[role] || role}
              </Typography>
              <Typography style={{ fontSize: 12.5, color: '#5c6370', marginTop: 4 }}>
                پرسش‌هایی که بیشترِ کاربرانِ همین نقش با آن‌ها روبه‌رو می‌شوند
              </Typography>
            </Box>
            <Divider />
            <CardContent>
              {visibleFaq.map((f, i) => (
                <Box key={i} className={classes.faq}>
                  <Box display="flex" justifyContent="space-between" alignItems="center"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                    <Typography style={{ fontWeight: 700, fontSize: 13, color: '#283593' }}>{f.q}</Typography>
                    <Typography style={{ color: '#9aa1b9', fontWeight: 800 }}>{openFaq === i ? '−' : '+'}</Typography>
                  </Box>
                  <Collapse in={openFaq === i}>
                    <Typography style={{ fontSize: 12.5, color: '#43476a', lineHeight: 1.9, marginTop: 6 }}>
                      {f.a}
                    </Typography>
                  </Collapse>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}
      </Box>

      {tourOpen && (
        <GuidedTour
          steps={TOUR_STEPS[role] || TOUR_STEPS.Contractor}
          onDone={() => {
            setTourOpen(false)
            if (role) localStorage.setItem('tour_done_' + role, '1')
          }}
        />
      )}
    </Layout>
  )
}

export default HelpPage
